import { ImageName } from "@mcleod/images";
import {
    Api, ArrayUtil, Currency, CurrencySettings, DateRange, FieldUpdateEvent, HorizontalAlignment,
    McLeodClassicIntegration, Model, ModelRow, Navigation, StringUtil, UrlUtil
} from "@mcleod/core";
import {
    AddType, BlurEvent, Button, ButtonVariant, Component, DataHeader, DataSource, DataSourceAction,
    DataSourceExecutionEvent, DataSourceMode, ForcedCase, Label, Layout, LookupModelSearchEvent, OpenType, Panel,
    SlideoutDecorator
} from "@mcleod/components";
import { CommonDialogs, ImageTable } from "@mcleod/common";
import {
    getDetentionControlBoolean, LMECompanySettings, LMEUserSettings, UsersResponsibilitySlideoutMF
} from "@mcleod/general";
import { CallRecordTable, DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { AutogenLayoutARInquiry } from "./autogen/AutogenLayoutARInquiry";
import { AutogenLayoutCustomer } from "./autogen/AutogenLayoutCustomer";
import { CreditOverrides } from "./CreditOverrides";
import { CustomerOptions } from "./CustomerOptions";
import { GoogleSearchDecorator, GoogleSearchDecoratorProps} from "@mcleod/dispatch";

const viewCustomerURL = "lme/ar/Customer?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "customer",
    screenName: "Customer",
    navigateToUrl: viewCustomerURL
};

const GOOGLE_SEARCH_PROPS: GoogleSearchDecoratorProps = {
    cityField: 'city',
    cityIdField: 'city_id',
    stateField: 'state_id',
    zipField: 'zip_code'
}

export class Customer extends AutogenLayoutCustomer {
    private validFileExtensions: string[] = [ ".tiff", ".png", ".jpg", ".jpeg", ".gif", ".bmp" ];
    codeGenerationIsActive: boolean;
    allowManualEntry: boolean;
    dialogTextboxProps = { forcedCase: ForcedCase.UPPER, tooltip: "Input will be truncated to eight characters" };

    override async onLoad() {
        new GoogleSearchDecorator(this, GOOGLE_SEARCH_PROPS);
        this.layoutContactTable.parentRowType = "C";
        this.layoutContactTable.defaultTypeOf = 'C';
        this.sourceCustomer.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceCustomerAfterGetDataboundValues(row, dataSource);
        this.setAllowedImageTypes();
        const companySettings = LMECompanySettings.getSingleton();
        if (!companySettings.isBrokerageLtl())
            this.hideLtlTabs();
        else {
            Model.searchSingleRecord("lme/powerbroker/contract-management-control", { vendor: "P44" }).then(response => {
                this.textboxP44ProfileId.visible = response?.get("is_active") == "Y";
            });
            Model.searchSingleRecord("lme/powerbroker/contract-management-control", { vendor: "SLA" }).then(response => {
                this.textboxSMC3ProfileId.visible = response?.get("is_active") == "Y";
            });
        }
        this.setupCreditOverrideTab();

        this.tabDetention.visible = getDetentionControlBoolean("is_active", false);
        const result = UrlUtil.getPropsFromUrl(window.location.href);
        if (result?.contact) {
            this.addLayoutDataLoadListener(() => {
                this.layoutContactTable.tableContacts.scrollIntoView();
                const row = this.layoutContactTable.tableContacts.rows.find(row => row.data.get('id') === result.contact);
                if (row) {
                    row.showEditLayout();
                }
            });
        }

        const allContacts = 'Y' === LMEUserSettings.get()?.all_contacts;
        if (!allContacts) {
            this.sourceContacts.addBeforeExecutionListener((event) => {
                event.filter.type_of = "C || B";
            });
        }
    }

    buttonOpenClassicOnClick() {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.ar.EntryCustomer", this.mainDataSource.activeRow.get("id"));
    }

    hideLtlTabs() {
        this.tabAutoRate.visible = false;
        this.tabBillofLading.visible = false;
    }

    textboxCategoryBeforeLookupModelSearch(event) {
        event.filter.customer = 'Y';
    }

    textboxOperationsUserBeforeLookupModelSearch(event) {
        event.filter.web_user_type = 'U';
    }

    displayCustomerUsersSlideout() {
        const urs = UsersResponsibilitySlideoutMF.builder()
                    .layoutName("lme/ar/CustomerUsers")
                    .title(this.getCustomersUsersSlideoutTitle())
                    .idFilter(this.getCustomerUsersIdFilter())
                    .parentMainDataSource(this.sourceCustomer)
                    .parentAgencyLinkDataSource(this.sourceAgencyLink)
                    .primaryAgencyField(this.getCustomerUsersAgencyField())
                    .build();
        urs.show();
    }

    private getCustomersUsersSlideoutTitle(): string {
        const name = this.sourceCustomer.activeRow?.get("name");
        return "Customer Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getCustomerUsersIdFilter(): any {
        const id = this.sourceCustomer.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getCustomerUsersAgencyField(): string {
        return "primary_agency";
    }

    sourceCustomerAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set primary_agency in search mode because the field isn't bound
            const primaryAgencyField = this.getCustomerUsersAgencyField();
            row.set(primaryAgencyField, dataSource.searchRow.get(primaryAgencyField));

            //have to add agency_link search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceAgencyLink.data))
                return;
            row.addLinkedModel({ model: this.sourceAgencyLink.url, rows: [this.sourceAgencyLink.data[0]] });
        }
    }

    sourceCustomerAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            (this.layoutCallRecord as CallRecordTable).customerId = this.activeRow?.get("id");
            (this.layoutImageTable as ImageTable).populateForRow("C", this.activeRow.get("id"));
            const customerId = this.activeRow?.get("id");
            if (customerId && this.dataHeader != null)
                this.dataHeader.afterClosePath = "lme/ar/Customer?id=" + customerId;
        }

        if (event?.getAction() == DataSourceAction.UPDATE) {
            this.textboxId.enabled = false;
            this.textboxId.required = true;
            const customerId = this.activeRow?.get("id");
            if (customerId && this.dataHeader != null)
                this.dataHeader.afterClosePath = "lme/ar/Customer?id=" + customerId;
        }

        if (event.getAction() == DataSourceAction.ADD) {
            this.textboxId.enabled = false;
        }

        // if we just created a new sell tariff for the customer on the server-side, then the lookup model data is out of sync and needs to be updated manually
        if (this.tabAutoRate.visible && !this.activeRow.isNull("sell_tariff_lookup")) {
            this.activeRow.setLookupModelData("sell_tariff_id", this.activeRow.get("sell_tariff_lookup"));
            this.textboxSellTariffId.displayData(this.activeRow, null, 0);
            this.activeRow.set("sell_tariff_lookup", undefined);
        }

        const currencySettings = CurrencySettings.getSingleton();
        this.layoutCustomerOptions.layoutCreditCollections.sourceCreditCalculations.search({
            customer_id: this.sourceCustomer.activeRow?.get("id"),
            currency_type: this.sourceCustomer.activeRow?.get("currency_type", currencySettings.getTransactionalCurrency())
         });

        this.layoutDetentionDefinitionTable.tableDetentionDefinition.allowAdvancedSearch = false;
        this.layoutDetentionDefinitionTable.labelDefinitions.visible = true;
        this.layoutDetentionDefinitionTable.tableDetentionDefinition.addType = AddType.NOT_ALLOWED;
        this.layoutDetentionDefinitionTable.tableDetentionDefinition.defaultOpenType = OpenType.NEW_TAB_WITH_SWITCH;

        const dateRange = DateRange.parseDateRangeWithKeywords("t-5,t").getFormattedString().split("-");
        const filter = {
            type: "C",
            customer_id: this.activeRow?.get("id"),
            customer_name: this.activeRow?.get("name"),
            status: "!V",
            creation_date: `>=${dateRange[0]}&<=${dateRange[1]}`
        };
        this.layoutDetentionSummary.setNewTabFilter(filter);
        this.sourceDetentionHistoryLookup.search({
            "customer_id": filter.customer_id,
            "status": filter.status,
            "creation_date": filter.creation_date
        });
    }

    buttonAllCallsOnClick() {
        const id = this.activeRow?.get("id");
        if (!StringUtil.isEmptyString(id)) {
            const layout = Layout.getLayout("lme/dispatch/CallRecordTable") as CallRecordTable;
            new SlideoutDecorator({
                layout: layout,
                width: window.innerWidth * .75,
                fillVerticalSpace: true,
                title: `Customer Sales Calls - ${this.activeRow.get("name")}`,
                layoutLoadListeners: () => layout.tableCallRecords.headerVisible = true,
                doAfterSlideIn: (decorator: SlideoutDecorator) => layout.mainDataSource.search({ "parent_row_type": "C", "parent_row_id": id })
            })
        }
    }

    unpostedOrdersOnClick() {
        const id = this.mainDataSource.activeRow?.get("id");
        if (id) {
            const layout = Layout.getLayout("lme/ar/ARInquiry") as AutogenLayoutARInquiry;
            new SlideoutDecorator({
                layout: layout,
                width: window.innerWidth * .75,
                fillVerticalSpace: true,
                title: "Customer Unposted Orders - " + this.mainDataSource.activeRow.get("name"),
                layoutLoadListeners: () => layout.panelSearch.visible = false,
                doAfterSlideIn: (decorator: SlideoutDecorator) => layout.mainDataSource.search({ "customer_id": id,  })
            });
        }
    }

    public static navigateTo(id: string): void {
        Navigation.navigateTo(viewCustomerURL + id, { newTab: true });
    }

    textboxSellTariffBeforeLookupModelSearch(event) {
        event.filter.tariff_type_code = 'S';
    }

    textboxBuyTariffBeforeLookupModelSearch(event) {
        event.filter.tariff_type_code = 'B';
    }

    textboxP44ProfileBeforeLookupModelSearch(event) {
        event.filter.config_type = 'N';
    }

    textboxSMC3ProfileIdBeforeLookupModelSearch(event) {
        event.filter.config_type = 'N';
    }

    private setAllowedImageTypes() {
        this.attachmentBlLogo["setAttachmentWithUI"] = async (file: File) => {
            const maxSize = this.attachmentBlLogo.maxSize;
            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.substring(fileName.lastIndexOf("."));
            if (!this.validFileExtensions.includes(fileExtension))
                return this.attachmentBlLogo.showAdhocTooltip("The file must be an image.", { shaking: true, timeout: 5000 });
            else if (maxSize != null && file.size > maxSize)
                return this.attachmentBlLogo.showAdhocTooltip("The file is too big to attach. The file must be smaller than " + this.attachmentBlLogo.maxSize / (1024 * 1024) + "MB.", { shaking: true, timeout: 5000 });
            else {
                this.attachmentBlLogo.setFile(file);
            }
        }
    }

    sourceCustomerBeforeModeChange(event) {
        this.buttonOpenClassic.visible = event.newMode === DataSourceMode.UPDATE;

        if (event.newMode === DataSourceMode.ADD) {
            this.textboxId.enabledDuringAdd = true;
            Api.post("lme/general/code-generation-settings", { "table_name": "customer" }).then(response => {
                this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
                this.allowManualEntry = response.data[0]?.allow_manual_entry ?? false;
                if (this.codeGenerationIsActive) {
                    this.textboxId.caption = "*Customer Code";
                    this.textboxId.enabledDuringAdd = this.allowManualEntry;
                }
                DuplicateId.configureIdTextbox(this.textboxId, {
                    ...DUPLICATE_VALIDATION_PROPS,
                    codeGenerationIsActive: this.codeGenerationIsActive
                });
            });
        }

        const isNotSearchMode = event.newMode !== DataSourceMode.SEARCH;
        this.buttonAllCalls.visible = isNotSearchMode;
        this.buttonReports.visible = isNotSearchMode;
    }

    sourceCustomerAfterModeChange(event) {
        this.tabImages.visible = !(event.newMode == DataSourceMode.SEARCH || event.newMode == DataSourceMode.ADD);
        if (event.newMode === DataSourceMode.UPDATE) {
            this.addTariffIdUpdateListenerToRow(this.activeRow);
        }
    }
    private addTariffIdUpdateListenerToRow(row: ModelRow) {
        if (row["_tarListener"] == null) {
            row["_tarListener"] = (event: FieldUpdateEvent) => {
                if (event.oldValue != null && event.newValue == null) {
                    if (event.fieldName === "sell_tariff_id") {
                        row.set("tar_uid", null);
                        this.sourceRateExceptions.clear();
                        this.sourceRateExceptions.displayDataInBoundComponents();
                    } else if (event.fieldName === "buy_tariff_id") {
                        row.set("cost_tar_uid", null);
                    }
                }
            }
            row.addAfterFieldUpdateListener(row["_tarListener"]);
        }
    }

    textboxCurrencyTypeOnLookupModelSelection() {
        this.updateCurrencyFields();
    }

    textboxCreditLimitOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            this.updateCurrencyFields();
        }
    }

    updateCurrencyFields() {
        const currencySettings = CurrencySettings.getSingleton();
        if (currencySettings.multiCurrencyEnabled()) {
            if (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.UPDATE) {
                const currencyType = this.mainDataSource.activeRow?.get("currency_type", currencySettings.getTransactionalCurrency());
                const creditLimit = this.mainDataSource.activeRow?.getCurrency("credit_limit", null);
                if (creditLimit != null) {
                    creditLimit.setCurrencyCode(currencyType);
                    this.mainDataSource.activeRow?.set("credit_limit", creditLimit);
                }
            } else if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                const currencyType = this.mainDataSource.searchRow?.get("currency_type", currencySettings.getFunctionalCurrency());
                const creditLimit = this.mainDataSource.searchRow?.get("credit_limit") as Currency;
                if (creditLimit != null && typeof creditLimit === "string") {
                    const amount = parseFloat(creditLimit);
                    if (isNaN(amount)) {
                        return;
                    }
                    const creditLimitCurrency = new Currency(amount);
                    creditLimitCurrency.setCurrencyCode(currencyType);
                    this.mainDataSource.searchRow?.set("credit_limit", creditLimitCurrency);
                } else if (creditLimit instanceof Currency) {
                    creditLimit.setCurrencyCode(currencyType);
                    this.mainDataSource.searchRow?.set("credit_limit", creditLimit);
                }
            }
        }
    }

    async checkAutogen(): Promise<any> {
        return await Api.post("lme/dispatch/check-manual-autogen",
            {
                "table_name": "customer",
                "table_row": this.mainDataSource?.activeRow?.data
            })
    }

    private duplicateCustomerLinkOnClick(customerCode: string) {
        Customer.navigateTo(customerCode);
    }

    private getDuplicateCustomerSnackbarPanel(customerId: string, description: string, autoGenerated: boolean): Panel {
        const labelProps = { color: "white", fontSize: "large", rowBreak: false, paddingLeft: 1, paddingRight: 1, marginLeft: 0, marginRight: 0 };

        const generatedString: string = autoGenerated ? " generated " : " ";
        let components: Component[] = [];
        if (!StringUtil.isEmptyString(customerId)) {
            components = [
                new Label({ ...labelProps, text: "The" + generatedString + "code [" }),
                new Label({ ...labelProps, text: customerId + " - " + description + " " }),
                new Button({ imageName: ImageName.detail, imageWidth: 20, imageHeight: 20, margin: 0, tooltip: "Click to go to Customer screen", color: "subtle.light", variant: ButtonVariant.round, onClick: () => this.duplicateCustomerLinkOnClick(customerId), rowBreak: false }),
                new Label({ ...labelProps, rowBreak: false, text: "] " }),
                new Label({ ...labelProps, text: "is a duplicate of an existing code.  Please enter a new one." })
            ];
        }
        return new Panel({ wrap: true, align: HorizontalAlignment.LEFT, fillRow: true, components: components, fillHeight: true });
    }

    showDuplicateCodeDialog(code: string, generated: boolean) {
        const generatedString: string = generated ? " generated " : " ";
        CommonDialogs.showInputDialog("Enter a new code", "Duplicate" + generatedString + "code [" + code + "]", this.dialogTextboxProps)
            .then(async (input) => {
                if (input) {
                    input = input.substring(0, 8);
                    this.textboxId.text = input;
                    this.mainDataSource.activeRow.set("id", input);
                    await this.checkManualCode()
                        .then((response) => {
                            if (response.data[0]?.is_duplicate) {
                                this.showDuplicateCodeDialog(input, false);
                            }
                        });
                } else {
                    this.textboxId.text = null;
                    this.mainDataSource.activeRow.set("id", null);
                }
            });
    }

    async checkManualCode(): Promise<any> {
        return await Api.post("lme/dispatch/asset/check-manual-customer-code",
            {
                "id": this.mainDataSource?.activeRow?.get("id")
            });
    }

    textboxInvoiceProcessingUserBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.web_user_type = "U";
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.showSave = true;
        dataHeader.showSaveAndClose = true;
    }

    setupCreditOverrideTab() {
        const creditOverrideTab = this.layoutCustomerOptions.tabCreditOverrides;
        creditOverrideTab.dataSource = this.sourceCreditOverride;
        const creditOverrides = this.layoutCustomerOptions.layoutCreditOverrides as CreditOverrides;
        creditOverrides.tableCreditOverride.dataSource = this.sourceCreditOverride;
    }

    sourceCreditOverrideAfterExecution() {
        const creditOverrideTab = this.layoutCustomerOptions.tabCreditOverrides;
        const customerOptionsLayout = this.layoutCustomerOptions as CustomerOptions;
        const rowCount = customerOptionsLayout.getOverrideRowCount();
        customerOptionsLayout.styleOverrideTab(creditOverrideTab, rowCount>0, rowCount);
    }

    invoicesDueReminderReportOnClick() {
        this.openReportScreen("lme/ar/reports/InvoicesDueReminderReport");
    }

    customerStatementsReportOnClick() {
        this.openReportScreen("lme/ar/reports/CustomerStatementsReport");
    }

    private openReportScreen(path: string) {
        const customerId = this.mainDataSource.data[0]?.get("id") as string;
        const finalizedPath = path + UrlUtil.buildQueryString(`customer_id=${customerId}`);
        Navigation.navigateTo(finalizedPath, { newTab: true });
    }
}
